import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Copy, Check, Loader as LoaderIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { Logo } from "@/components/layout/PublicLayout";
import { AuthenticatorAppLinks } from "@/components/portal/AuthenticatorAppLinks";

const STEPS = 4;

export default function EnforceTwoFactor() {
  const { t } = useLang();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 App, 2 QR, 3 Code, 4 Backup
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [savedConfirmed, setSavedConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const sessionExpired = () => {
    toast.error(t("Zeitfenster abgelaufen. Bitte melden Sie sich erneut an.", "Time window expired. Please sign in again."));
    localStorage.removeItem("eleven_token");
    navigate("/login");
  };

  const startSetup = async () => {
    setBusy(true);
    try {
      const res = await api.post("/auth/2fa/setup", {});
      setSetupData(res.data);
      setStep(2);
    } catch (err) {
      if (err.response?.status === 401) return sessionExpired();
      toast.error(err.response?.data?.detail || t("Fehler beim Starten", "Error starting setup"));
    } finally {
      setBusy(false);
    }
  };

  const activate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/auth/2fa/activate", { code });
      // Pflicht erfuellt: volles Sitzungs-Token ersetzt das Enroll-Token
      if (res.data.access_token) {
        localStorage.setItem("eleven_token", res.data.access_token);
        localStorage.setItem("eleven_user", JSON.stringify(res.data.user));
      }
      setBackupCodes(res.data.backup_codes || []);
      setStep(4);
    } catch (err) {
      if (err.response?.status === 401) return sessionExpired();
      toast.error(err.response?.data?.detail || t("Ung\u00fcltiger Code", "Invalid code"));
    } finally {
      setBusy(false);
    }
  };

  const finish = () => {
    const stored = localStorage.getItem("eleven_user");
    if (stored) setUser(JSON.parse(stored));
    toast.success(t("2FA aktiviert. Willkommen!", "2FA enabled. Welcome!"));
    navigate("/portal");
  };

  const copyBackupCodes = async () => {
    try {
      await navigator.clipboard.writeText(backupCodes.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("Kopieren fehlgeschlagen", "Copy failed"));
    }
  };

  return (
    <div className="min-h-screen dark-section noise-overlay flex items-center justify-center px-4 py-10">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md">
        <div className="flex justify-center">
          <Logo dark />
        </div>
        <Card className="mt-8 rounded-[20px] border-white/10 bg-white/[0.06] backdrop-blur-md">
          <CardContent className="p-8">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-[12px] bg-[#FF3C20]/12 flex items-center justify-center shrink-0">
                <ShieldCheck className="h-5 w-5 text-[#FF3C20]" />
              </div>
              <div>
                <h1 className="font-display text-lg font-semibold text-white">
                  {t("Konto absichern: 2FA einrichten", "Secure your account: set up 2FA")}
                </h1>
                <p className="text-xs text-white/50">
                  {t("Ihr Administrator hat die Zwei-Faktor-Authentifizierung f\u00fcr Ihr Konto vorgeschrieben.", "Your administrator requires two-factor authentication for your account.")}
                </p>
              </div>
            </div>

            {/* Schritt-Anzeige */}
            <div className="mt-5 flex items-center gap-1.5" data-testid="enroll-step-indicator">
              {Array.from({ length: STEPS }).map((_, i) => (
                <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i < step ? "bg-[#FF3C20]" : "bg-white/10"}`} />
              ))}
            </div>
            <p className="mt-1.5 text-[11px] text-white/40">{t(`Schritt ${step} von ${STEPS}`, `Step ${step} of ${STEPS}`)}</p>

            {step === 1 && (
              <div className="mt-5 space-y-4" data-testid="enroll-step-1">
                <p className="text-sm text-white/80">
                  {t("1. Installieren Sie eine Authenticator-App auf Ihrem Smartphone \u2013 falls noch nicht vorhanden:", "1. Install an authenticator app on your smartphone \u2013 if you don't have one yet:")}
                </p>
                <AuthenticatorAppLinks dark />
                <Button onClick={startSetup} disabled={busy} className="w-full rounded-[12px] bg-[#FF3C20] text-white hover:bg-[#E8241B] active:scale-[0.98]" data-testid="enroll-continue-step1">
                  {busy ? <LoaderIcon className="h-4 w-4 animate-spin" /> : t("App ist installiert \u2013 weiter", "App is installed \u2013 continue")}
                </Button>
              </div>
            )}

            {step === 2 && setupData && (
              <div className="mt-5 space-y-4" data-testid="enroll-step-2">
                <p className="text-sm text-white/80">
                  {t("2. \u00d6ffnen Sie die App, tippen Sie auf \u201eKonto hinzuf\u00fcgen\u201c und scannen Sie diesen QR-Code:", "2. Open the app, tap \u2018Add account\u2019 and scan this QR code:")}
                </p>
                <div className="flex justify-center">
                  <img src={setupData.qr_data_uri} alt="2FA QR-Code" className="h-44 w-44 rounded-[12px] bg-white p-2" data-testid="enroll-qr-image" />
                </div>
                <div className="rounded-[10px] bg-white/5 border border-white/10 px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wide text-white/40">{t("Kein Scan m\u00f6glich? Schl\u00fcssel manuell eingeben:", "Can't scan? Enter key manually:")}</p>
                  <p className="font-mono text-xs text-white/80 break-all select-all" data-testid="enroll-manual-secret">{setupData.secret}</p>
                </div>
                <Button onClick={() => setStep(3)} className="w-full rounded-[12px] bg-[#FF3C20] text-white hover:bg-[#E8241B] active:scale-[0.98]" data-testid="enroll-continue-step2">
                  {t("Gescannt \u2013 weiter", "Scanned \u2013 continue")}
                </Button>
              </div>
            )}

            {step === 3 && (
              <form onSubmit={activate} className="mt-5 space-y-4" data-testid="enroll-step-3">
                <p className="text-sm text-white/80">
                  {t("3. Geben Sie den 6-stelligen Code ein, den die App jetzt anzeigt:", "3. Enter the 6-digit code the app shows now:")}
                </p>
                <div className="space-y-1.5">
                  <Label className="text-white/80">{t("6-stelliger Code", "6-digit code")}</Label>
                  <Input
                    required inputMode="numeric" maxLength={6} placeholder="123456" autoFocus
                    value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    className="rounded-[10px] bg-white/5 border-white/15 text-white text-center text-lg tracking-[0.3em] placeholder:tracking-normal placeholder:text-white/30"
                    data-testid="enroll-code-input"
                  />
                </div>
                <Button type="submit" disabled={busy || code.length !== 6} className="w-full rounded-[12px] bg-[#FF3C20] text-white hover:bg-[#E8241B] active:scale-[0.98]" data-testid="enroll-activate-submit">
                  {busy ? <LoaderIcon className="h-4 w-4 animate-spin" /> : t("Aktivieren", "Activate")}
                </Button>
                <button type="button" onClick={() => setStep(2)} className="w-full text-center text-xs text-white/50 hover:text-white transition-colors" data-testid="enroll-back-step2">
                  {t("Zur\u00fcck zum QR-Code", "Back to QR code")}
                </button>
              </form>
            )}

            {step === 4 && (
              <div className="mt-5 space-y-4" data-testid="enroll-step-4">
                <p className="text-sm text-white/80">
                  {t("4. Speichern Sie diese Backup-Codes an einem sicheren Ort. Jeder Code funktioniert genau einmal, falls Sie keinen Zugriff auf Ihre App haben. Sie werden NUR JETZT angezeigt.", "4. Store these backup codes in a safe place. Each code works exactly once if you lose access to your app. They are shown ONLY NOW.")}
                </p>
                <div className="grid grid-cols-2 gap-2" data-testid="enroll-backup-codes">
                  {backupCodes.map((c) => (
                    <div key={c} className="rounded-[8px] bg-white/5 border border-white/10 px-3 py-2 font-mono text-sm text-white text-center select-all">{c}</div>
                  ))}
                </div>
                <Button variant="outline" onClick={copyBackupCodes} className="w-full rounded-[10px] border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="enroll-copy-backup-codes">
                  {copied ? <Check className="h-4 w-4 mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />}
                  {copied ? t("Kopiert", "Copied") : t("Alle Codes kopieren", "Copy all codes")}
                </Button>
                <label className="flex items-start gap-2 text-sm text-white/80 cursor-pointer">
                  <Checkbox checked={savedConfirmed} onCheckedChange={(v) => setSavedConfirmed(!!v)} className="mt-0.5 border-white/30" data-testid="enroll-saved-checkbox" />
                  <span>{t("Ich habe die Backup-Codes sicher gespeichert.", "I have stored the backup codes safely.")}</span>
                </label>
                <Button onClick={finish} disabled={!savedConfirmed} className="w-full rounded-[12px] bg-[#FF3C20] text-white hover:bg-[#E8241B] active:scale-[0.98]" data-testid="enroll-finish-button">
                  {t("Fertig \u2013 zum Portal", "Done \u2013 go to portal")}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
